// externals
import { routerMiddleware } from 'connected-react-router';
import {
    autoSelectProgramTelemetryMiddleware,
    equityAllocationBackfillMiddleware,
    updatedPenciledInputsMiddleware
} from '@makemydeal/dr-offer-redux';
import { apiMiddleware } from 'redux-api-middleware';

// utils
import { historyInstance } from '@makemydeal/dr-shared-store';
import { instrumentedThunkMiddleware } from './instrumentedThunkMiddleware';

// middleware
import * as cdlV3AnalyticsMiddleware from '@makemydeal/dr-dash-store/middleware/cdlV3AnalyticsMiddleware';
import * as correlationMiddleware from '@makemydeal/dr-dash-store/middleware/correlationMiddleware';
import * as creditDecisionMiddleware from '@makemydeal/dr-dash-store/middleware/creditDecisionMiddleware';
import * as dealTemplateMiddleware from '@makemydeal/dr-dash-store/middleware/dealTemplateMiddleware';
import * as dueAtSigningMiddleware from '@makemydeal/dr-dash-store/middleware/dueAtSigningMiddleware';
import { loadCdlV3ScriptMiddleware } from '@makemydeal/dr-dash-store/middleware/loadCdlV3ScriptMiddleware';
import { middleware as incompleteTradeModalMiddleware } from '@makemydeal/dr-dash-store/middleware/incompleteTradeModalMiddleware';
import * as offerMiddleware from '@makemydeal/dr-dash-store/middleware/offerMiddleware';
import * as orderMiddleware from '@makemydeal/dr-dash-store/middleware/orderMiddleware';
import * as paymentRaceTelemetryMiddleware from '@makemydeal/dr-dash-store/middleware/paymentRaceTelemetryMiddleware';
import * as paymentSequenceMiddleware from '@makemydeal/dr-dash-store/middleware/paymentSequenceMiddleware';
import * as rollToOptionsMiddleware from '@makemydeal/dr-dash-store/middleware/rollToOptionsMiddleware';
import * as sellRateFailureRollbackMiddleware from '@makemydeal/dr-dash-store/middleware/sellRateFailureRollbackMiddleware';
import * as socketMiddleware from '@makemydeal/dr-dash-store/middleware/socketMiddleware';
import * as purchaseHistoryMiddleware from '@makemydeal/dr-dash-store/middleware/purchaseHistoryMiddleware';
import * as tradeInIntelligenceMiddleware from '@makemydeal/dr-dash-store/middleware/tradeInIntelligenceMiddleware';
import * as dealPulseSlideoutMiddleware from '@makemydeal/dr-dash-store/middleware/dealPulseSlideoutMiddleware';
import * as proposalMiddleware from '@makemydeal/dr-dash-store/middleware/proposalMiddleware';
import * as quoteModeTelemetryMiddleware from '@makemydeal/dr-dash-store/middleware/quoteModeTelemetryMiddleware';
import * as firstPencilInterceptMiddleware from '@makemydeal/dr-dash-store/middleware/firstPencilInterceptMiddleware';
import * as leaseContractingFieldsMiddleware from '@makemydeal/dr-dash-store/middleware/leaseContractingFieldsMiddleware';
import { stateSnapshotTelemetryMiddleware } from '@makemydeal/dr-dash-store/middleware/stateSnapshotTelemetryMiddleware';
import { selectorDeltaTelemetryMiddleware } from '@makemydeal/dr-dash-store/middleware/selectorDeltaTelemetryMiddleware';
import { bootstrapMiddleware } from '../middleware/bootstrapMiddleware';

export const getInitialMiddleware = () => [
    instrumentedThunkMiddleware,
    incompleteTradeModalMiddleware, // Must run BEFORE offerMiddleware and apiMiddleware to intercept SAVE_OFFER
    correlationMiddleware.correlationMiddleware,
    apiMiddleware,
    // Completes the ReduxAction spine: emits a lightweight row for RSAA
    // lifecycle FSAs (OFFER_SAVE_SUCCESS, PAYMENT_RECEIVED/FAILURE) that
    // redux-api-middleware dispatches via next() downstream of
    // correlationMiddleware and which therefore never reach it. MUST run after
    // apiMiddleware. Gated on enableReduxTelemetry (same as correlationMiddleware).
    correlationMiddleware.reduxActionSpineMiddleware,
    // ── Payment race measurement, then the guard. Both ORDERING-CRITICAL. ──────────────────────────────
    //
    // paymentRaceTelemetryMiddleware must stay UPSTREAM of the guard. It emits OutOfOrderPaymentResolved,
    // the metric that measures how often the underlying race happens; downstream of the guard it would
    // never see a discarded response, so the metric would read zero whether the guard was working
    // correctly, comparing the wrong lane, or discarding responses it should have kept. The guard's own
    // PaymentSequenceDiscarded event is what reports its activity.
    //
    // paymentSequenceMiddleware discards a superseded payment response by refusing to call next(), so it
    // must run UPSTREAM of every middleware that reacts to PAYMENT_RECEIVED. A middleware registered before
    // it still receives the response from its own next(action) call and acts on it — which is why the guard
    // lives here, near the top of the chain, and not next to the payment middlewares in
    // getRemainingMiddleware(). It is downstream of correlationMiddleware/apiMiddleware so responses are
    // fully enriched, and downstream of incompleteTradeModalMiddleware, whose PAYMENT_RECEIVED handler runs
    // BEFORE its next() and so must keep seeing every response to avoid losing a pending save.
    //
    // Importing the guard also installs its fail-open pending-operation registrations
    // (paymentSequencePendingOperations), so nothing here has to import a reactor to keep that net populated.
    paymentRaceTelemetryMiddleware.middleware,
    paymentSequenceMiddleware.middleware,
    bootstrapMiddleware,
    routerMiddleware(historyInstance),
    offerMiddleware.middleware,
    orderMiddleware.middleware,
    socketMiddleware.middleware,
    purchaseHistoryMiddleware.middleware,
    tradeInIntelligenceMiddleware.middleware,
    dealPulseSlideoutMiddleware.middleware,
    creditDecisionMiddleware.middleware,
    rollToOptionsMiddleware.middleware,
    sellRateFailureRollbackMiddleware.middleware,
    dealTemplateMiddleware.middleware,
    quoteModeTelemetryMiddleware.middleware,
    firstPencilInterceptMiddleware.middleware,
    dueAtSigningMiddleware.middleware,
    proposalMiddleware.middleware,
    loadCdlV3ScriptMiddleware,
    cdlV3AnalyticsMiddleware.cdlV3AnalyticsEventMiddleware,
    updatedPenciledInputsMiddleware,
    equityAllocationBackfillMiddleware,
    autoSelectProgramTelemetryMiddleware,
    leaseContractingFieldsMiddleware.middleware
];

/**
 * Tail telemetry middlewares, appended AFTER all business middleware by the
 * store configurer (`configureStore`). Kept OUT of `getInitialMiddleware`
 * (and `getRemainingMiddleware`) so business middleware can never be
 * accidentally ordered after them — the configurer guarantees they run last,
 * in this order:
 *
 *   selectorDeltaTelemetryMiddleware  (US2040385) — walks the selector registry
 *   stateSnapshotTelemetryMiddleware  (US2039705) — captures post-reducer state
 *
 * Both MUST observe the same post-reducer state on the same dispatch, so the
 * order here is significant; the store-configurer unit test asserts it. Both
 * are default-OFF via the `enableStateSnapshotTelemetry` toggle.
 */
export const getTelemetryMiddleware = () => [selectorDeltaTelemetryMiddleware, stateSnapshotTelemetryMiddleware];
